import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { pickDefaultPaymentMethod } from "@/components/organisms/PaymentSettings/helpers";
import { isSameFlightNumber } from "@/helpers/bookingHelpers";
import { clientPaymentApi } from "@/redux/apis/clientPayment/clientPaymentApi";
import { ClientPaymentMethod } from "@/redux/apis/clientPayment/types";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import {
  ActiveBookingField,
  BookingPassenger,
  BookingPassengerDetail,
  ClientBookingExtra,
  ClientBookingState,
  ClientBookingTransferType,
  ClientBookingVehicle,
  DepartureTerminalSource,
  GuestCardDisplay,
  GuestContact,
  PickupTerminalSource,
  PickupTimeSource,
} from "./types";

export const initialState: ClientBookingState = {
  pickup: null,
  destination: null,
  stops: [],
  activeField: null,
  isPinning: false,
  pickupFlightNumber: null,
  pickupFlightDelayMinutes: null,
  pickupFlightLandingUsed: null,
  pickupFlightDerivedArrival: null,
  pickupFlightDerivedArrivalSource: null,
  pickupFlightFrozenAt: null,
  pickupFlightFrozenReason: null,
  pickupFlightChangedSinceCommit: false,
  pickupTerminalSource: null,
  departureFlightNumber: null,
  departureFlightChangedSinceCommit: false,
  departureTerminalSource: null,
  pickupTimeSource: null,
  startNewGuest: false,
  passenger: null,
  sessionGuests: [],
  pickupDatetime: null,
  paymentMethod: null,
  guestPaymentMethodId: null,
  guestPaymentMethodCountry: null,
  guestPaymentMethodCard: null,
  guestContact: null,
  storedQuote: null,
};

/** First unfilled field in visual order (pickup → stops → destination), or null when all are set. */
export const firstEmptyField = (
  pickup: PointsOfInterest | null,
  stops: (PointsOfInterest | null)[],
  destination: PointsOfInterest | null
): ActiveBookingField | null => {
  if (!pickup) return { kind: "pickup" };
  const emptyStop = stops.findIndex((stop) => stop === null);
  if (emptyStop !== -1) return { kind: "stop", index: emptyStop };
  if (!destination) return { kind: "destination" };
  return null;
};

/** Logical equality for booking fields (same kind, and same index for stops). */
export const isSameField = (a: ActiveBookingField | null, b: ActiveBookingField | null): boolean => {
  if (!a || !b) return a === b;
  if (a.kind !== b.kind) return false;
  return a.kind !== "stop" || b.kind !== "stop" || a.index === b.index;
};

/**
 * Everything measured against the DEPARTURE flight, dropped together (DEV-1487).
 *
 * Shared by the three events that invalidate it — a new drop-off, "Not Flying", and the destination
 * being typed away from — because a departure flight number captured for one airport says nothing
 * about another, and the terminal badge would go on naming a swap that no longer describes the
 * address beside it. `pickupTimeSource` goes too: with no departure flight there is no SUGGESTED
 * PICKUP for a later one to be refused in favour of.
 *
 * `pickupDatetime` is deliberately LEFT, exactly as `clearPickupFlight` leaves it — the client keeps
 * the time they chose rather than being sent back to an empty field.
 */
const clearDepartureFields = (state: ClientBookingState) => {
  state.departureFlightNumber = null;
  state.departureTerminalSource = null;
  state.departureFlightChangedSinceCommit = false;
  state.pickupTimeSource = null;
};

const writeField = (state: ClientBookingState, field: ActiveBookingField, poi: PointsOfInterest | null) => {
  if (field.kind === "pickup") {
    state.pickup = poi;
    // A new pickup invalidates any captured flight number (mirrors mobile reset), and with it the
    // delay measured against that flight's arrival.
    state.pickupFlightNumber = null;
    state.pickupFlightDelayMinutes = null;
    state.pickupFlightLandingUsed = null;
    state.pickupTerminalSource = null;
  } else if (field.kind === "destination") {
    state.destination = poi;
    clearDepartureFields(state);
  } else if (field.kind === "stop" && field.index >= 0 && field.index < state.stops.length) state.stops[field.index] = poi;
};

/**
 * Client booking draft — the shared contract for the booking sheet's Address step.
 * The teammate's BookingSummaryPanel reads from here (via selectors); LocationPanel,
 * the map pin picker, and the suggestions list all write into it. Ephemeral (not persisted).
 */
export const clientBookingSlice = createSlice({
  name: "clientBooking",
  initialState,
  reducers: {
    setPickup: (state, action: PayloadAction<PointsOfInterest | null>) => {
      state.pickup = action.payload;
      // A new pickup invalidates any captured flight number (mirrors mobile reset), and with it the
      // delay measured against that flight's arrival.
      state.pickupFlightNumber = null;
      state.pickupFlightDelayMinutes = null;
      state.pickupFlightLandingUsed = null;
      state.pickupFlightFrozenReason = null;
      state.pickupFlightChangedSinceCommit = false;
      // The pickup the client just chose IS their choice, so there is no longer a swap to explain.
      state.pickupTerminalSource = null;
    },
    setPickupFlightNumber: (state, action: PayloadAction<string | null>) => {
      // A different flight carries a different disruption. The stored reason describes the flight the
      // BOOKED pickup was built on, so leaving it attached would print "this flight has been cancelled"
      // under a perfectly healthy replacement.
      // A different flight landed at a different time, so the recorded operand no longer describes the
      // pickup. Left attached, it would be offered to the server as the landing that produced a pickup
      // built from the PREVIOUS flight — refused on create, and on edit it clears a good applied arrival.
      //
      // Compared with `isSameFlightNumber`, the shared rule, rather than raw equality. The modal trims and
      // upper-cases while a rehydrated booking carries the raw payload value, so "qf1424" or "QF 1424"
      // against a stored "QF1424" read as a DIFFERENT flight — which cleared a frozen reason that is still
      // true and discarded a landing that still describes the pickup. Re-confirming the same number must
      // clear nothing, and this is the one place that decides it.
      if (!isSameFlightNumber(action.payload, state.pickupFlightNumber)) {
        state.pickupFlightFrozenReason = null;
        state.pickupFlightLandingUsed = null;
        // The committed pickup was built on the flight being replaced. See `pickupFlightChangedSinceCommit`
        // for why this is a flag rather than nulling the pickup outright.
        state.pickupFlightChangedSinceCommit = true;
        // The badge names the flight the terminal came from; a different flight makes that sentence
        // false, and the swap re-runs against the new one anyway.
        state.pickupTerminalSource = null;
        // Capturing an ARRIVAL flight withdraws the drop-off's SUGGESTED PICKUP (DEV-1487): the arrival
        // owns the time from here, so a marker saying the committed pickup came from the suggestion —
        // or that the client refused it — describes an offer that is no longer on the panel. Left set,
        // a later "Not Flying" would flip the booking back into the departure's case with its own
        // suggestion already blocked.
        state.pickupTimeSource = null;
      }
      state.pickupFlightNumber = action.payload;
    },
    /**
     * "Not Flying" — this is no longer a flight booking. Web's counterpart to mobile's `resetFlightNumber`.
     *
     * Load-bearing rather than cosmetic: the disrupted Date & Time panel offers no time fields, because
     * both real outcomes of a cancelled flight have a home — a new flight, or not flying. If this exit
     * does nothing, a passenger on a cancelled flight has no route to any pickup time at all.
     *
     * Everything measured against the flight goes with it: the wait is meaningless without an arrival, the
     * landing operand describes a flight no longer on the booking, and the stored disruption described that
     * same departed flight.
     *
     * `pickupDatetime` is deliberately LEFT. The client keeps the time they booked rather than being sent
     * back to an empty field — matching the sibling Continue path, which nulls the datetime only when the
     * flight number actually CHANGED. On create every field here is already null, so this is a no-op and
     * "Not Flying" remains a plain dismiss.
     */
    clearPickupFlight: (state) => {
      state.pickupFlightNumber = null;
      state.pickupFlightDelayMinutes = null;
      state.pickupFlightLandingUsed = null;
      state.pickupFlightFrozenReason = null;
      // No flight left to derive against, and the booked time deliberately stays — so there is nothing for
      // a re-derivation to reopen.
      state.pickupFlightChangedSinceCommit = false;
      // "Not Flying" flips the booking into the case where the DROP-OFF's suggestion is offered
      // (DEV-1487). Cleared so that suggestion gets a fresh hearing: whatever this marker held was
      // decided while the arrival owned the pickup, which is no longer true.
      state.pickupTimeSource = null;
      // The pickup stays where it is, but with no flight on the booking there is nothing left to
      // attribute it to.
      state.pickupTerminalSource = null;
    },
    /**
     * Move the pickup to the arrival terminal the flight lookup reported, KEEPING every `pickupFlight*`
     * field (DEV-1594).
     *
     * Its own reducer because every other pickup setter clears those fields — correctly, since every other
     * pickup change is the client choosing a different place, which invalidates a flight number captured
     * for the old one. This one is the opposite: the flight is the reason the pickup moved, so clearing it
     * would discard the captured number, the booked wait and the landing the committed pickup was derived
     * from — and then re-prompt the Flight Details modal for the number that caused the move.
     *
     * Idempotent by construction rather than by a guard here: the swapped-in POI carries the flight's
     * terminal and IATA in its label, so `pickupIsAtFlightTerminal` holds the caller's effect afterwards.
     */
    swapPickupForTerminal: (state, action: PayloadAction<{ pickup: PointsOfInterest; source: PickupTerminalSource }>) => {
      state.pickup = action.payload.pickup;
      state.pickupTerminalSource = action.payload.source;
    },
    /**
     * Commit "Delay driver arrival by" (minutes). Persisted so the server can evaluate
     * `pickup = flight arrival + delay` and so editing a flight booking rehydrates the delay the
     * client actually booked instead of silently resetting it to 0 (DEV-1429). `null` = not asked.
     */
    setPickupFlightDelayMinutes: (state, action: PayloadAction<number | null>) => {
      state.pickupFlightDelayMinutes = action.payload;
    },
    /**
     * Commit the landing the pickup was derived from, for `flight_landing_used`. Dispatched from the same
     * handler that commits the pickup and the delay, because the server checks the three as one claim
     * (`landing + delay*60 === pickup`) and a value committed on its own could only disagree with them.
     */
    setPickupFlightLandingUsed: (state, action: PayloadAction<string | null>) => {
      state.pickupFlightLandingUsed = action.payload;
    },
    setDestination: (state, action: PayloadAction<PointsOfInterest | null>) => {
      state.destination = action.payload;
      // Mirrors the pickup branch of `setPickup`: a new drop-off invalidates the departure flight
      // captured for the old one. See `clearDepartureFields`.
      clearDepartureFields(state);
    },
    /**
     * Commit the DEPARTURE flight number captured for an airport drop-off. Twin of
     * `setPickupFlightNumber`, and compared with the same shared `isSameFlightNumber` for the same
     * reason: a rehydrated booking carries the raw payload value ("qf 994") while the modal trims and
     * upper-cases, so raw equality reads a no-op re-confirm as a change.
     *
     * A REAL change reopens the SUGGESTED PICKUP. `pickupTimeSource` goes back to null so the new
     * flight's own recommendation applies — including over a `"manual"` from the previous flight,
     * which described a departure that is no longer on the booking. A hand edit made AFTER this
     * change is what sets it back to "manual", and that is the one the panel must not overwrite.
     *
     * The booked DATE survives, as it does on the arrival side (DEV-1510): only the derivation re-runs.
     */
    setDepartureFlightNumber: (state, action: PayloadAction<string | null>) => {
      if (!isSameFlightNumber(action.payload, state.departureFlightNumber)) {
        state.departureFlightChangedSinceCommit = true;
        state.pickupTimeSource = null;
        // The badge names the flight the terminal came from; a different flight makes that sentence
        // false, and the swap re-runs against the new one anyway.
        state.departureTerminalSource = null;
      }
      state.departureFlightNumber = action.payload;
    },
    /** "Not Flying" on an airport drop-off — see `clearDepartureFields` for what goes and what stays. */
    clearDepartureFlight: (state) => {
      clearDepartureFields(state);
    },
    /**
     * Move the DROP-OFF to the terminal the departure flight leaves from, KEEPING `departureFlightNumber`
     * (DEV-1487). Its own reducer for the same reason `swapPickupForTerminal` is: every other destination
     * setter clears the departure fields, correctly, because every other destination change is the client
     * choosing a different place. This one is the opposite — the flight is the reason the drop-off moved.
     */
    swapDestinationForTerminal: (state, action: PayloadAction<{ destination: PointsOfInterest; source: DepartureTerminalSource }>) => {
      state.destination = action.payload.destination;
      state.departureTerminalSource = action.payload.source;
    },
    /** Record how the committed pickup time was arrived at — see {@link PickupTimeSource}. */
    setPickupTimeSource: (state, action: PayloadAction<PickupTimeSource | null>) => {
      state.pickupTimeSource = action.payload;
    },
    /** Appends an empty stop input (filled later via setStop). */
    addStop: (state) => {
      state.stops.push(null);
    },
    setStop: (state, action: PayloadAction<{ index: number; poi: PointsOfInterest | null }>) => {
      const { index, poi } = action.payload;
      if (index >= 0 && index < state.stops.length) state.stops[index] = poi;
    },
    removeStop: (state, action: PayloadAction<number>) => {
      const removed = action.payload;
      state.stops.splice(removed, 1);
      // Keep activeField pointing at the same stop after the splice shifts indices, or clear it if
      // the active stop was the one removed — otherwise a later write/pin targets the wrong stop or no-ops.
      const field = state.activeField;
      if (field?.kind === "stop") {
        if (field.index === removed) state.activeField = null;
        else if (field.index > removed) state.activeField = { kind: "stop", index: field.index - 1 };
      }
    },
    setActiveField: (state, action: PayloadAction<ActiveBookingField | null>) => {
      // No-op when logically unchanged: consumers key effects on this object's identity, and a
      // focus re-fire (window refocus, dialog restore) must not read as a field change.
      if (!isSameField(state.activeField, action.payload)) state.activeField = action.payload;
    },
    /**
     * Write a location into whichever field is currently active (suggestion tap / pin Select),
     * then advance activeField to the next empty field so the user is walked through the form
     * (pickup → stops → destination); when nothing is left to fill, activeField clears.
     */
    setActiveFieldLocation: (state, action: PayloadAction<PointsOfInterest | null>) => {
      const field = state.activeField;
      if (!field) return;
      writeField(state, field, action.payload);
      if (action.payload) state.activeField = firstEmptyField(state.pickup, state.stops, state.destination);
    },
    /**
     * Write a location into a SPECIFIC field — for picks whose resolution is async (Google place
     * details), where focus may have moved on by the time the result lands. Advances activeField
     * only when the written field is still the active one; a stale write never steals focus.
     */
    setFieldLocation: (state, action: PayloadAction<{ field: ActiveBookingField; poi: PointsOfInterest | null }>) => {
      const { field, poi } = action.payload;
      writeField(state, field, poi);
      if (poi && isSameField(state.activeField, field)) state.activeField = firstEmptyField(state.pickup, state.stops, state.destination);
    },
    setIsPinning: (state, action: PayloadAction<boolean>) => {
      state.isPinning = action.payload;
    },
    /**
     * Mark (or clear) this draft as a "New Guest" booking (DEV-1610). Set by the picker CTA before
     * navigating to the sheet; the Passenger sub-panel reads it to open its new-guest form, then clears
     * it so returning to the passenger step later shows the normal picker.
     */
    setStartNewGuest: (state, action: PayloadAction<boolean>) => {
      state.startNewGuest = action.payload;
    },
    /** Set (or clear, `null` ⇒ back to self) the selected passenger. */
    setBookingPassenger: (state, action: PayloadAction<BookingPassenger | null>) => {
      state.passenger = action.payload;
    },
    /** Add a one-off guest for this session (not saved to favourites). */
    addSessionGuest: (state, action: PayloadAction<BookingPassenger>) => {
      state.sessionGuests.push(action.payload);
    },
    /** Set (or clear) the pickup date + time (ISO string). */
    setPickupDatetime: (state, action: PayloadAction<string | null>) => {
      state.pickupDatetime = action.payload;
      // Whatever is committed now was built against the flight currently selected, so the staleness this
      // flag records is spent. Cleared on a null too: with no committed pickup the panel re-derives anyway.
      state.pickupFlightChangedSinceCommit = false;
      state.departureFlightChangedSinceCommit = false;
    },
    /** Per-booking card/invoice choice — never touches the account default. */
    setPaymentMethod: (state, action: PayloadAction<ClientPaymentMethod | null>) => {
      state.paymentMethod = action.payload;
    },
    /**
     * Guest flow only: the Stripe PaymentMethod id (`pm_…`) tokenised client-side via
     * `stripe.createPaymentMethod`, paired with the card's 2-letter issuing country
     * (`paymentMethod.card.country`, e.g. "PH"/"AU" — may be omitted/null) and display metadata
     * (brand/last4/expiry, for the confirmation screen's Payment box). The guest has no
     * account/saved cards, so this replaces the `paymentMethod` selection; the server attaches the pm
     * to the account it mints at submit. Clearing the id (`id: null`, e.g. after a failed submit)
     * always clears the country and card too — they're never meaningfully out of sync.
     */
    setGuestPaymentMethodId: (
      state,
      action: PayloadAction<{ id: string | null; country?: string | null; card?: GuestCardDisplay | null }>
    ) => {
      state.guestPaymentMethodId = action.payload.id;
      state.guestPaymentMethodCountry = action.payload.id ? action.payload.country ?? null : null;
      state.guestPaymentMethodCard = action.payload.id ? action.payload.card ?? null : null;
    },
    /**
     * Guest flow only: the guest's own contact details from the passenger step. Feeds the
     * account-mint fields (email/first_name/last_name/phone/title) on the guest booking-create body.
     */
    setGuestContact: (state, action: PayloadAction<GuestContact | null>) => {
      state.guestContact = action.payload;
    },
    /**
     * Guest flow only: replace the draft with a previously stashed one — resumes a booking
     * after an ACCOUNT_EXISTS 409 routed the guest to login. Merges onto initialState (so any fields
     * dropped from the stash reset cleanly) while preserving the current scope binding.
     */
    restoreBookingDraft: (state, action: PayloadAction<Partial<ClientBookingState>>) => ({
      ...initialState,
      ...action.payload,
      scopeAccountId: state.scopeAccountId,
    }),
    /**
     * Bind the draft to a booking scope (the managed client's id under operator OBO,
     * `null` in self-service). Resets the whole draft when the scope changes so one
     * client's locations/passenger/payment can never leak into another's; a no-op when
     * the scope is unchanged, so re-entering the same scope keeps the in-progress draft.
     */
    initBookingScope: (state, action: PayloadAction<string | null>) => {
      if ((state.scopeAccountId ?? null) === action.payload) return state;
      return { ...initialState, scopeAccountId: action.payload };
    },
    /** Set (or clear) the selected vehicle (Vehicle and Transfer step). */
    setBookingVehicle: (state, action: PayloadAction<ClientBookingVehicle | null>) => {
      state.vehicle = action.payload;
    },
    /** Set (or clear) the selected transfer type (`listed_events_list`). */
    setBookingTransferType: (state, action: PayloadAction<ClientBookingTransferType | null>) => {
      state.transferType = action.payload;
    },
    /** Set the passenger count. */
    setNumOfPassengers: (state, action: PayloadAction<number>) => {
      state.numOfPassengers = action.payload;
    },
    /** Set the declared bag count. */
    setNumOfBags: (state, action: PayloadAction<number>) => {
      state.numOfBags = action.payload;
    },
    /** Replace the selected extras. */
    setBookingExtras: (state, action: PayloadAction<ClientBookingExtra[]>) => {
      state.extras = action.payload;
    },
    /** Set the "Booked by" free-text (Other Details step). */
    setBookedBy: (state, action: PayloadAction<string>) => {
      state.bookedBy = action.payload;
    },
    /** Set the client reference (Other Details step). */
    setReference: (state, action: PayloadAction<string>) => {
      state.reference = action.payload;
    },
    /** Set the additional notes to the driver (`additional_info`). */
    setAdditionalInfo: (state, action: PayloadAction<string>) => {
      state.additionalInfo = action.payload;
    },
    /** Replace the per-passenger detail rows (Other Details step). */
    setPassengerDetails: (state, action: PayloadAction<BookingPassengerDetail[]>) => {
      state.passengers = action.payload;
    },
    /** Booking rate type, derived from the selected transfer type (`journey_rate` | `hourly_rate` | `daily_rate` | `fixed_*`). */
    setJobRate: (state, action: PayloadAction<string | undefined>) => {
      state.jobRate = action.payload;
    },
    /** Duration in minutes for hourly/daily transfer types (0/undefined for journey). */
    setBookedForDuration: (state, action: PayloadAction<number | undefined>) => {
      state.bookedForDuration = action.payload;
    },
    /** Operator-entered price for fixed-rate transfer types (undefined clears it when switching to an auto-quoted rate). */
    setFixedPrice: (state, action: PayloadAction<number | undefined>) => {
      state.fixedPrice = action.payload;
    },
    clearBooking: () => initialState,
  },
  extraReducers: (builder) => {
    // Auto-preselect the account default payment method when the list loads,
    // but only if the client hasn't picked one yet — a manual selection (or a
    // freshly added card) must survive refetches. Mirrors the mobile booking
    // slice's getPaymentMethods matcher, minus its always-overwrite behaviour.
    builder.addMatcher(clientPaymentApi.endpoints.getClientPaymentMethods.matchFulfilled, (state, action) => {
      // Ignore a fulfillment fetched for a different account than this draft's scope. Under
      // operator OBO the same endpoint also serves other clients' Manage-Client Settings pages,
      // and an in-flight fetch for a previous scope can land after the draft was rebound — that
      // would preselect the wrong client's card. Self-service has no scope and only ever fetches
      // its own account, so it always applies.
      if (state.scopeAccountId && action.meta.arg.originalArgs !== state.scopeAccountId) return;
      // View-only replays a finished booking's exact payment method — never auto-preselect the
      // account default over it (that caused a flash of the default card/invoice before hydration).
      if (state.viewOnly) return;
      // Preselect the account default only when nothing is chosen yet. Never overwrite an
      // explicit selection: this matcher fires globally (any payment-methods fetch, incl.
      // the add-card refetch or a Settings-page load), and clobbering would silently swap
      // the card the client picked — or drop a just-added one mid-webhook-lag.
      if (state.paymentMethod !== null) return;
      // Prefer the account default; fall back to the first method so a list with no flagged default still
      // auto-selects rather than leaving the payment step blank. Expired cards are skipped either way
      // (DEV-1596) — an expired default must never be silently booked with; nothing usable stays null.
      state.paymentMethod = pickDefaultPaymentMethod(action.payload);
    });
  },
});

export default clientBookingSlice.reducer;
export const {
  setPickup,
  setPickupFlightNumber,
  clearPickupFlight,
  swapPickupForTerminal,
  setDepartureFlightNumber,
  clearDepartureFlight,
  swapDestinationForTerminal,
  setPickupTimeSource,
  setPickupFlightDelayMinutes,
  setPickupFlightLandingUsed,
  setDestination,
  addStop,
  setStop,
  removeStop,
  setActiveField,
  setActiveFieldLocation,
  setFieldLocation,
  setIsPinning,
  setStartNewGuest,
  setBookingPassenger,
  addSessionGuest,
  setPickupDatetime,
  setPaymentMethod,
  setGuestPaymentMethodId,
  setGuestContact,
  restoreBookingDraft,
  initBookingScope,
  setBookingVehicle,
  setBookingTransferType,
  setNumOfPassengers,
  setNumOfBags,
  setBookingExtras,
  setBookedBy,
  setReference,
  setAdditionalInfo,
  setPassengerDetails,
  setJobRate,
  setBookedForDuration,
  setFixedPrice,
  clearBooking,
} = clientBookingSlice.actions;
